import { convertInteractionDisplayName, type PerformanceFilter } from "features/Library/Common/utils/performanceUtils";
import { type FC, useEffect } from "react";
import { type ColumnOption } from "interfaces/columnOptions";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { type SelectedView, type UserAssetTypes, type UserReportDetailsWithRoute } from "./types";

import { useReportingFormatWithTimeZone } from "../../../../hooks/useReportingFormatWithTimeZone";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { useQuery } from "@tanstack/react-query";
import { getUserDetailsQuery } from "./Queries/queries";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { type ConnectedProps, connect } from "react-redux";

import styles from "./userPerformanceDrilldown.module.scss";
import { MoboLink } from "components/MoboLink/MoboLink";
import moment from "moment";

export type Props = PropsFromRedux & {
  type: UserAssetTypes;
  view: SelectedView;
  userId: number;
  filter: PerformanceFilter;
};

export const UserPerformanceDrilldown: FC<Props> = ({ type, userId, view, filter, setHasExportData }) => {
  const formatDateTime = useReportingFormatWithTimeZone();
  const detailsQuery = useQuery({
    queryKey: ["detailsQuery", filter, userId, type, view],
    queryFn: getUserDetailsQuery,
    enabled: type !== undefined,
  });

  const columnOptions: ColumnOption<UserReportDetailsWithRoute>[] = [
    {
      name: "TITLE",
      width: 5,
      isSortable: false,
      render: (entity: UserReportDetailsWithRoute) => {
        return (
          <MoboLink to={`/${entity.Route}/${entity.Id}`} className={styles.linkButton}>
            <Tooltip target={<TextTruncate>{entity.Title}</TextTruncate>} content={entity.Title} />
          </MoboLink>
        );
      },
    },
    {
      name: "DATE",
      width: 5,
      isSortable: false,
      render: (entity: UserReportDetailsWithRoute) => {
        const date = moment.utc(`${entity.Date} ${entity.Time}`);
        return <div>{formatDateTime(date)}</div>;
      },
    },
  ];

  useEffect(() => {
    setHasExportData({
      hasDataToExport: detailsQuery.isSuccess && !!detailsQuery.data.length,
      tooltipContent: "No data available for export. This user contains no data.",
    });
  }, [setHasExportData, detailsQuery.isSuccess, detailsQuery.data]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2>{convertInteractionDisplayName(type)}</h2>
      </div>
      <RequestStatusRenderer state={detailsQuery.status}>
        <GenericPerformanceList rows={detailsQuery.data!} columns={columnOptions} filter={filter} key="key" />
      </RequestStatusRenderer>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(UserPerformanceDrilldown);
export default ConnectedComponent;
